/**
 * Where the OAuth round trip lands.
 *
 * Normally invisible: the backend redirects the popup here, this reports the
 * outcome to the window that opened it and closes. The rendered body only ever
 * shows when someone reaches the URL without a popup to report to -- pop-ups
 * blocked, or a link opened by hand.
 */

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";

import {
  OAUTH_MESSAGE,
  OAuthResult,
  connectErrorMessage,
} from "@/providers/external/connect";

export default function OAuthCallback() {
  const [params] = useSearchParams();
  // Snapshotted once: window.opener can be cleared as the window closes, and
  // re-reading it mid-teardown would flip this to "not a popup" and flash the
  // fallback message on the way out.
  const [isPopup] = useState(() => !!window.opener && window.opener !== window);
  const [closeFailed, setCloseFailed] = useState(false);

  const status = params.get("status") === "ok" ? "ok" : "error";
  const returnTo = params.get("return_to") || "/";

  useEffect(() => {
    if (!isPopup) return;

    const result: OAuthResult = {
      status,
      connection: params.get("connection") ?? undefined,
      system: params.get("system") ?? undefined,
      reason: params.get("reason") ?? undefined,
    };

    window.opener.postMessage(
      { type: OAUTH_MESSAGE, ...result },
      window.location.origin,
    );
    window.close();
    // close() is not guaranteed to be honoured. If it is not, show the message
    // below rather than leaving a blank tab behind.
    const timer = setTimeout(() => setCloseFailed(true), 400);
    return () => clearTimeout(timer);
  }, [isPopup, params, status]);

  if (isPopup && !closeFailed) return null;

  const problem =
    status === "ok"
      ? null
      : (connectErrorMessage({
          status,
          reason: params.get("reason") ?? undefined,
        }) ?? "Connection was cancelled.");

  return (
    <div className="flex min-h-svh items-center justify-center p-8">
      <div className="max-w-sm text-center">
        <p className="text-sm">
          {problem ?? "Connected. You can close this window."}
        </p>
        <Link
          to={returnTo}
          className="mt-4 inline-block text-sm text-primary underline-offset-4 hover:underline"
        >
          Back to Eventera
        </Link>
      </div>
    </div>
  );
}
